// client/src/pages/ProfilePage.js

import React, { useState, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";
import { useTranslation } from 'react-i18next';

import { updateMyProfile } from '../services/userService';

export default function ProfilePage() {
  const { t } = useTranslation();
  // Extraer datos iniciales del token (si incluye name y email)
  const token = localStorage.getItem('token');
  let decoded = null;
  try {
    decoded = jwtDecode(token);
  } catch {
    decoded = null;
  }
  const initialName = decoded?.name || '';
  const initialEmail = decoded?.email || '';

  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Validar contraseñas coincidan (solo si quieren cambiarla)
    if (password || passwordConfirm) {
      if (!currentPassword) {
        setError(t('profile.error.currentPasswordRequired'));
        return;
      }
      if (password !== passwordConfirm) {
        setError(t('profile.error.passwordMatch'));
        return;
      }
      if (password.length < 6) {
        setError(t('profile.error.passwordLength'));
        return;
      }
    }

    try {
      // Llamamos al servicio con los campos que hayan cambiado
      const payload = {};
      if (name.trim() !== initialName) payload.name = name.trim();
      if (email.trim().toLowerCase() !== initialEmail) payload.email = email.trim().toLowerCase();
      if (password) {
        payload.password = password;
        payload.currentPassword = currentPassword;
      }

      if (Object.keys(payload).length === 0) {
        setMessage(t('profile.noChanges'));
        return;
      }

      await updateMyProfile(payload);
      setMessage(t('profile.success'));

      // Limpiar los campos de contraseña tras un cambio correcto.
      setCurrentPassword('');
      setPassword('');
      setPasswordConfirm('');

      // Si quieres actualizar el token en localStorage para reflejar el nuevo email/name,
      // tendrías que pedir al backend que devuelva un JWT nuevo.
      // O, simplemente recargas la página para que jwtDecode coja los datos actualizados.
    } catch (err) {
      console.error('[ProfilePage] Error al actualizar perfil:', err);
      // Mostrar el mensaje del backend si lo hay (p. ej. "contraseña actual incorrecta").
      setError(err.message || t('profile.error.update'));
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>{t('profile.title')}</h2>
      {message && <p style={styles.success}>{message}</p>}
      {error && <p style={styles.error}>{error}</p>}
      <form onSubmit={handleSubmit} style={styles.form}>
        <label style={styles.label}>
          {t('profile.name')}:
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            required
          />
        </label>

        <label style={styles.label}>
          {t('profile.email')}:
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
        </label>

        <label style={styles.label}>
          {t('profile.currentPassword')}:
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={styles.input}
            autoComplete="current-password"
            placeholder={t('profile.currentPasswordPlaceholder')}
          />
        </label>

        <label style={styles.label}>
          {t('profile.newPassword')}:
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            autoComplete="new-password"
            placeholder={t('profile.passwordPlaceholder')}
          />
        </label>

        <label style={styles.label}>
          {t('profile.confirmPassword')}:
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            style={styles.input}
            placeholder={t('profile.confirmPasswordPlaceholder')}
          />
        </label>

        <button type="submit" style={styles.button}>
          {t('profile.saveButton')}
        </button>
      </form>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '400px',
    margin: '2rem auto',
    padding: '1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  },
  title: {
    textAlign: 'center',
    marginBottom: '1rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '0.9rem',
    color: '#333',
  },
  input: {
    marginTop: '0.25rem',
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
  button: {
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#423ef4',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  success: {
    color: 'green',
    textAlign: 'center',
  },
  error: {
    color: 'red',
    textAlign: 'center',
  },
};
